/**
 * Outgoing Message Validation Hook
 * Validates outgoing messages before sending
 * Different rules than incoming: checks user permissions, rate limits, message format
 */
import { type IPipelineContext, type HookResult } from 'edge-stream-js';
import { type HookMetadata } from '../HookMetadata';

export class ValidationHook {
  name = 'OutgoingValidationHook';
  priority = 20;

  metadata?: HookMetadata = {
    name: 'OutgoingValidationHook',
    description: 'Validates outgoing messages before sending. Checks author, text length (1-10000 chars), and message format',
    version: '1.0.0',
    author: 'EdgeStream',
    tags: ['validation', 'outgoing', 'security'],
    modifiesData: false,
    impactLevel: 'critical',
  };

  async execute(context: IPipelineContext): Promise<HookResult> {
    const envelope = context.envelope as any;
    const messageId = envelope.meta.id;
    const body = envelope.body as any;
    const messageData = body?.data?.data || {};

    console.log(`[OutgoingValidationHook] 🔍 Validating outgoing message: ${messageId}`);

    // Validate required fields for outgoing messages
    const errors: string[] = [];

    if (!messageData.author) {
      errors.push('Author is required');
    }
    if (!messageData.text) {
      errors.push('Message text is required');
    }
    if (messageData.text && messageData.text.trim().length === 0) {
      errors.push('Message cannot be empty or whitespace only');
    }

    // Outgoing-specific validation: message length
    if (messageData.text && messageData.text.length < 1) {
      errors.push('Message must be at least 1 character');
    }
    if (messageData.text && messageData.text.length > 10000) {
      errors.push('Outgoing messages are limited to 10000 characters');
    }

    // Outgoing-specific validation: author format
    if (messageData.author && messageData.author.length > 100) {
      errors.push('Author name must be less than 100 characters');
    }

    if (errors.length > 0) {
      console.warn(`[OutgoingValidationHook] ❌ Validation failed: ${errors.join(', ')}`);
      return {
        continue: false,
        error: `Outgoing validation failed: ${errors.join(', ')}`,
      };
    }

    console.log(`[OutgoingValidationHook] ✅ Outgoing message validated: ${messageId}`);
    return { continue: true };
  }
}
